import React, { useState } from 'react'
import { Box, Button, Menu, MenuItem, Popover, Typography, TextField } from '@mui/material'
import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { v4 as uuidv4 } from 'uuid'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { advancedColors } from '../../theme'
import { ProviderRoleId, labelForRole } from '../../currentRole'
import { draggedItem } from './ItemTag'
import PlannerCell, { PlacedItem, dropCell } from './PlannerCell'

export const CONTENT_PLANNER_MONTHS = 5
const WEEKS_PER_MONTH = 4
const DAYS_PER_WEEK = 7

const MONTH_LABELS = Array.from({ length: CONTENT_PLANNER_MONTHS }, (_, index) => `Month ${index + 1}`)
const WEEK_LABELS = Array.from({ length: WEEKS_PER_MONTH }, (_, index) => `Week ${index + 1}`)
const DAY_LABELS = Array.from({ length: DAYS_PER_WEEK }, (_, index) => `Day ${index + 1}`)

const placeholderOptionsFor = (role: ProviderRoleId): string[] => {
  const label = labelForRole(role)
  return [`${label} Focus A`, `${label} Focus B`, `${label} Focus C`]
}

const monthId = (monthIndex: number) => `month-${monthIndex}`
const weekRowKey = (monthIndex: number, weekIndex: number) => `month-${monthIndex}-week-${weekIndex}`

export type WeeklyPlannerData = {
  expandedMonthIds: string[]
  itemsByRowKey: Record<string, PlacedItem[]>
}

export const emptyWeeklyPlannerData = (): WeeklyPlannerData => ({ expandedMonthIds: [], itemsByRowKey: {} })

type Props = {
  role: ProviderRoleId
  data: WeeklyPlannerData
  onChange: (next: WeeklyPlannerData) => void
  editable: boolean
}

type PickerState = { rowKey: string; day: number; anchorEl: HTMLElement }
type CommentDraft = { rowKey: string; itemId: string; anchorEl: HTMLElement; text: string }

const borderedCellSx = { border: `1px solid ${advancedColors.line}`, p: 0 }
const monthHeaderSx = { ...borderedCellSx, backgroundColor: advancedColors.cardBg }

const WeeklyContentPlanner = ({ role, data, onChange, editable }: Props) => {
  const [pickerState, setPickerState] = useState<PickerState | null>(null)
  const [commentDraft, setCommentDraft] = useState<CommentDraft | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  )

  const expandedMonthIds = data.expandedMonthIds ?? []
  const itemsByRowKey = data.itemsByRowKey ?? {}

  const toggleMonth = (id: string) =>
    onChange({
      ...data,
      expandedMonthIds: expandedMonthIds.includes(id)
        ? expandedMonthIds.filter((candidate) => candidate !== id)
        : [...expandedMonthIds, id],
    })

  const placeItem = (rowKey: string, startDay: number, option: string) =>
    onChange({
      ...data,
      itemsByRowKey: {
        ...itemsByRowKey,
        [rowKey]: [...(itemsByRowKey[rowKey] ?? []), { id: uuidv4(), option, startMonth: startDay, months: 1 }],
      },
    })

  const removeItem = (rowKey: string, itemId: string) =>
    onChange({
      ...data,
      itemsByRowKey: {
        ...itemsByRowKey,
        [rowKey]: (itemsByRowKey[rowKey] ?? []).filter((item) => item.id !== itemId),
      },
    })

  const moveItem = (rowKey: string, itemId: string, startDay: number) =>
    onChange({
      ...data,
      itemsByRowKey: {
        ...itemsByRowKey,
        [rowKey]: (itemsByRowKey[rowKey] ?? []).map((item) =>
          item.id === itemId ? { ...item, startMonth: startDay } : item
        ),
      },
    })

  const setItemComment = (rowKey: string, itemId: string, comment: string) =>
    onChange({
      ...data,
      itemsByRowKey: {
        ...itemsByRowKey,
        [rowKey]: (itemsByRowKey[rowKey] ?? []).map((item) =>
          item.id === itemId ? { ...item, comment: comment.trim() || undefined } : item
        ),
      },
    })

  const handleAddClick = (rowKey: string, day: number) => (event: React.MouseEvent<HTMLElement>) =>
    setPickerState({ rowKey, day, anchorEl: event.currentTarget })

  const handleCommentClick = (rowKey: string) => (item: PlacedItem, event: React.MouseEvent<HTMLElement>) =>
    setCommentDraft({ rowKey, itemId: item.id, anchorEl: event.currentTarget, text: item.comment ?? '' })

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const dragged = draggedItem(active)
    const target = over ? dropCell(over) : undefined
    if (!dragged || !target || dragged.rowKey !== target.rowKey) {
      return
    }
    moveItem(dragged.rowKey, dragged.itemId, target.month)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={monthHeaderSx} />
              {DAY_LABELS.map((label) => (
                <Box component="th" key={label} sx={{ ...monthHeaderSx, p: 1, textAlign: 'left' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: advancedColors.ink }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {MONTH_LABELS.map((monthLabel, monthIndex) => {
              const id = monthId(monthIndex)
              const expanded = expandedMonthIds.includes(id)

              return (
                <React.Fragment key={id}>
                  <Box component="tr">
                    <Box component="th" sx={monthHeaderSx}>
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleMonth(id)}
                        onKeyDown={(event) => event.key === 'Enter' && toggleMonth(id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          cursor: 'pointer',
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, color: advancedColors.ink }}>{monthLabel}</Typography>
                        <ExpandMoreRoundedIcon
                          fontSize="small"
                          sx={{
                            color: advancedColors.inkMuted,
                            transform: expanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 120ms',
                          }}
                        />
                      </Box>
                    </Box>
                    <Box component="td" colSpan={DAYS_PER_WEEK} sx={borderedCellSx} />
                  </Box>

                  {expanded &&
                    WEEK_LABELS.map((weekLabel, weekIndex) => {
                      const rowKey = weekRowKey(monthIndex, weekIndex)
                      const items = itemsByRowKey[rowKey] ?? []

                      return (
                        <Box component="tr" key={rowKey}>
                          <Box component="th" sx={{ ...borderedCellSx, px: 1, py: 0.5, pl: 4 }}>
                            <Typography sx={{ color: advancedColors.inkMuted }}>{weekLabel}</Typography>
                          </Box>
                          {DAY_LABELS.map((_, dayIndex) => (
                            <PlannerCell
                              // eslint-disable-next-line react/no-array-index-key -- fixed 7-day week, days are never reordered
                              key={dayIndex}
                              rowKey={rowKey}
                              month={dayIndex}
                              items={items.filter((item) => item.startMonth === dayIndex)}
                              colSpan={1}
                              editable={editable}
                              onAddClick={handleAddClick(rowKey, dayIndex)}
                              onCommentClick={handleCommentClick(rowKey)}
                              onRemoveClick={(item) => removeItem(rowKey, item.id)}
                            />
                          ))}
                        </Box>
                      )
                    })}
                </React.Fragment>
              )
            })}
          </Box>
        </Box>
      </DndContext>

      <Menu anchorEl={pickerState?.anchorEl} open={!!pickerState} onClose={() => setPickerState(null)}>
        {placeholderOptionsFor(role).map((option) => (
          <MenuItem
            key={option}
            onClick={() => {
              if (pickerState) {
                placeItem(pickerState.rowKey, pickerState.day, option)
              }
              setPickerState(null)
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>

      <Popover
        anchorEl={commentDraft?.anchorEl}
        open={!!commentDraft}
        onClose={() => setCommentDraft(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.5, width: 260 }}>
          <TextField
            autoFocus
            multiline
            minRows={2}
            fullWidth
            placeholder="Add a comment"
            value={commentDraft?.text ?? ''}
            onChange={(event) => setCommentDraft((prev) => (prev ? { ...prev, text: event.target.value } : prev))}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button size="small" onClick={() => setCommentDraft(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                if (commentDraft) {
                  setItemComment(commentDraft.rowKey, commentDraft.itemId, commentDraft.text)
                }
                setCommentDraft(null)
              }}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default WeeklyContentPlanner
